import { useState, useEffect, useMemo, useRef, Suspense, lazy } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { MainApp } from "@/components/MainApp";
import { FeatureFlagDebugger } from "@/components/FeatureFlagDebugger";
import { FeatureFlagController } from "@/components/FeatureFlagController";
import { AuthSimplified } from "@/components/AuthSimplified";
import { PasswordResetForm } from "@/components/PasswordResetForm";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ErrorScreen } from "@/components/ErrorScreen";
import { AdminPanelNavigation } from "@/components/AdminPanelNavigation";
import { NavigationSkeleton } from "@/components/skeleton/NavigationSkeleton";
import { DevToolsPanel } from "@/components/DevToolsPanel";
import { MobileLoadingOptimizer } from "@/components/MobileLoadingOptimizer";
// Temporarily disabled performance monitoring components
// import { BudgetEnforcer } from "@/components/BudgetEnforcer";
// import { DevPerformancePanel } from "@/components/DevPerformancePanel";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isFeatureEnabled } from "@/config/featureFlags";
import { useAnonymousUser } from "@/hooks/useAnonymousUser";
import { cacheManager } from "@/utils/performance";
import { JoinNetworkModal } from "@/components/JoinNetworkModal";
import { useJoinModal } from "@/hooks/useJoinModal";
import { ServiceWorkerStatus } from "@/components/ServiceWorkerStatus";
import { NetworkDebugInfo } from "@/components/NetworkDebugInfo";
// import { initializePerformanceMonitor } from "@/utils/performanceBudgets";

const Index = () => {
  const { isLeftRailCollapsed = false } = useOutletContext<{ isLeftRailCollapsed?: boolean }>() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || "home");
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [requireAuth, setRequireAuth] = useState(false);
  const { toast} = useToast();
  const { anonymousUser } = useAnonymousUser();
  
  // Update tab when URL param changes
  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);
  
  const isLoginRequired = false; // App is open for browsing, login only for profile management

  // Join modal for anonymous users
  const isHomePage = activeTab === "home";
  
  const {
    isModalOpen,
    closeModal,
    handleCreateProfile,
    handleBrowseFirst
  } = useJoinModal(!!user, isHomePage);

  // Initialize performance monitoring - temporarily disabled
  // useEffect(() => {
  //   initializePerformanceMonitor('lite');
  // }, []);

  const onCreateProfile = () => {
    handleCreateProfile();
    setRequireAuth(true); // Trigger auth flow
  };

  const onBrowseFirst = () => {
    handleBrowseFirst();
    // Just close modal, let user continue browsing
  };

  useEffect(() => {
    console.log("=== INDEX COMPONENT MOUNTED ===");
    console.log("=== LOGIN REQUIRED ===", isLoginRequired);

    console.log("=== SETTING UP SERVICE WORKER UPDATE NOTIFICATIONS ===");
    
    // Service Worker update notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATED') {
          console.log("=== SW UPDATE DETECTED ===");
          toast({
            title: "App Updated! 🚀",
            description: "A new version is available. Reload to get the latest features and fixes.",
            action: (
              <button 
                onClick={() => {
                  console.log("=== RELOADING FOR SW UPDATE ===");
                  window.location.reload();
                }}
                className="bg-primary text-primary-foreground px-3 py-1 rounded text-sm font-medium hover:bg-primary/90"
              >
                Reload Now
              </button>
            ),
            duration: 10000, // Show for 10 seconds
          });
        }
      });
    }

    console.log("=== SETTING UP AUTH ===");
    
    // Check if this is a password reset flow (supports query and hash fragments)
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const type = urlParams.get('type');

    const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const hashAccessToken = hashParams.get('access_token') || hashParams.get('token_hash');
    const hashType = hashParams.get('type');

    const isRecovery = type === 'recovery' || hashType === 'recovery';
    const hasToken = !!(accessToken || hashAccessToken);

    if (isRecovery && hasToken) {
      console.log("=== PASSWORD RESET DETECTED (query/hash) ===");
      setIsPasswordReset(true);
    }
    
    // Set up auth state listener with bulletproof error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("=== AUTH STATE CHANGE ===", event, !!session);
        
        try {
          // Handle password recovery event specifically
          if (event === 'PASSWORD_RECOVERY') {
            setIsPasswordReset(true);
          }

          // Handle social login verification (defer Supabase calls to avoid async in callback)
          if (event === 'SIGNED_IN' && session?.user) {
            const provider = (session.user as any).app_metadata?.provider as string | undefined;
            if (provider && ['facebook', 'google', 'twitter'].includes(provider)) {
              setTimeout(async () => {
                try {
                  const { error } = await supabase
                    .from('profiles')
                    .upsert({
                      user_id: session.user!.id,
                      is_verified: true,
                      updated_at: new Date().toISOString()
                    }, { onConflict: 'user_id' });
                    
                  if (!error) {
                    toast({
                      title: "Account Verified ✓",
                      description: `Your account has been automatically verified through ${provider} login.`,
                    });
                  }
                } catch (error) {
                  console.warn('Social login verification failed (non-critical):', error);
                }
              }, 0);
            }
          }
          
          // Always fall back to anonymous-first on any auth issue
          setSession(session ?? null);
          setUser(session?.user ?? null);

          // If signed out or session missing, force navigation to Home tab (anonymous feed)
          if (event === 'SIGNED_OUT' || !session?.user) {
            setActiveTab('home');
          }

          setLoading(false);
          setAuthError(null);
        } catch (error) {
          // Any auth state change error should not break the app
          console.warn('Auth state change error (falling back to anonymous):', error);
          setSession(null);
          setUser(null);
          setActiveTab('home'); // Force anonymous mode
          setLoading(false);
          setAuthError(null); // Don't show auth errors to user
        }
      }
    );

   // Get initial session with bulletproof error handling
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      console.log("=== INITIAL SESSION ===", !!session, error);
      
      try {
        if (error) {
          console.warn("Session error (falling back to anonymous):", error);
          // Don't set authError - just fall back to anonymous
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Validate session if it exists
        if (session) {
          // Check if token is expired or about to expire
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
          const now = Date.now();
          const timeUntilExpiry = expiresAt - now;
          
          // If token expires in less than 5 minutes, try to refresh
          if (timeUntilExpiry < 5 * 60 * 1000) {
            console.log("=== TOKEN EXPIRING SOON, ATTEMPTING REFRESH ===");
            try {
              const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
              if (refreshError || !refreshData.session) {
                console.warn("Token refresh failed (falling back to anonymous):", refreshError);
                setSession(null);
                setUser(null);
              } else {
                setSession(refreshData.session);
                setUser(refreshData.session.user);
              }
            } catch (refreshError) {
              console.warn("Token refresh threw error (falling back to anonymous):", refreshError);
              setSession(null);
              setUser(null);
            }
          } else {
            // Session is valid
            setSession(session);
            setUser(session.user);
          }
        } else {
          // No session - anonymous mode
          setSession(null);
          setUser(null);
        }
        
        setLoading(false);
      } catch (sessionError) {
        console.warn("Session validation error (falling back to anonymous):", sessionError);
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    }).catch((err) => {
      console.warn("=== SESSION FETCH ERROR (falling back to anonymous) ===", err);
      // Never set authError for session issues - always fall back to anonymous
      setSession(null);
      setUser(null);
      setLoading(false);
    });

    // Listen for auth requirement events
    const handleRequireAuth = () => {
      console.log("=== AUTH REQUIRED EVENT ===");
      setRequireAuth(true);
    };

    window.addEventListener('requireAuth', handleRequireAuth);

    return () => {
      console.log("=== AUTH CLEANUP ===");
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
      window.removeEventListener('requireAuth', handleRequireAuth);
    };
  }, [isLoginRequired]);

  const handleAuthSuccess = () => {
    console.log("=== AUTH SUCCESS ===");
    setRequireAuth(false); // Reset auth requirement after success
  };

  const handleBrowseAnonymously = () => {
    console.log("=== BROWSE ANONYMOUSLY ===");
    setRequireAuth(false); // Allow browsing without auth
    setActiveTab("home"); // Go back to home feed
  };

  const handlePasswordResetComplete = () => {
    console.log("=== PASSWORD RESET COMPLETE ===");
    setIsPasswordReset(false);
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleRetry = () => {
    console.log("=== RETRYING CONNECTION ===");
    setAuthError(null);
    setLoading(true);
    
    // Bulletproof retry - always fall back to anonymous on any error
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.warn("Retry failed (continuing in anonymous mode):", error);
        setSession(null);
        setUser(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);
    }).catch((err) => {
      console.warn("Retry threw error (continuing in anonymous mode):", err);
      setSession(null);
      setUser(null);
      setLoading(false);
    });
  };

  // Bulletproof fallback: if loading takes too long or errors occur, proceed with anonymous mode
  const fallbackTriggeredRef = useRef(false);
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      if (fallbackTriggeredRef.current || !loading) return;
      fallbackTriggeredRef.current = true;
      console.log("=== LOADING TIMEOUT – FALLING BACK TO ANONYMOUS MODE ===");
      
      // Always succeed with anonymous mode
      try {
        supabase.auth.getSession().then(({ data: { session } }) => {
          setSession(session ?? null);
          setUser(session?.user ?? null);
        }).catch(() => {
          // Even if getSession fails, continue in anonymous mode
          setSession(null);
          setUser(null);
        }).finally(() => {
          setLoading(false);
        });
      } catch (error) {
        // Absolutely nothing should prevent anonymous mode
        console.warn("Even fallback failed, forcing anonymous mode:", error);
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [loading]);

  // Memoize expensive computations to prevent re-renders
  const authState = useMemo(() => ({
    loading, 
    user: !!user, 
    session: !!session, 
    authError,
    isPasswordReset
  }), [loading, user, session, authError, isPasswordReset]);

  // Only log when state actually changes, not on every render
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("=== INDEX STATE CHANGE ===", authState);
    }
  }, [authState.loading, authState.user, authState.session, authState.authError, authState.isPasswordReset]);

  // Show a simple fallback if there are issues
  if (typeof window === 'undefined') {
    console.log("=== WINDOW UNDEFINED ===");
    return <div>Loading...</div>;
  }

  if (loading) {
    console.log("=== SHOWING LOADING SCREEN ===");
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to">
        <LoadingScreen authError={authError} onRetry={handleRetry} />
        <NavigationSkeleton />
      </div>
    );
  }

  if (authError && authError.includes("critical")) {
    console.log("=== SHOWING ERROR SCREEN ===");
    return <ErrorScreen authError={authError} onRetry={handleRetry} />;
  }

  // Show password reset form if user is authenticated via password reset
  if (user && session && isPasswordReset) {
    console.log("=== SHOWING PASSWORD RESET FORM ===");
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to">
        <PasswordResetForm onComplete={handlePasswordResetComplete} />
        <AdminPanelNavigation />
      </div>
    );
  }

  if ((isLoginRequired && !user && !session) || requireAuth) { // Show auth when required or requested
    console.log("=== SHOWING AUTH SCREEN ===");
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to">
        <AuthSimplified onAuthSuccess={handleAuthSuccess} onBrowseAnonymously={handleBrowseAnonymously} />
        <AdminPanelNavigation />
      </div>
    );
  }

  console.log("=== SHOWING MAIN APP ===");
  console.log("🖼️ Index wrapper - checking background setup");
  return (
    <div className="min-h-screen relative">
      <MobileLoadingOptimizer />
      <MainApp activeTab={activeTab} onTabChange={setActiveTab} isLeftRailCollapsed={isLeftRailCollapsed} />
      <JoinNetworkModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onCreateProfile={onCreateProfile}
        onBrowseFirst={onBrowseFirst}
      />
      {/* Unified Dev Tools Panel - all debuggers in one column */}
      <DevToolsPanel />
      <AdminPanelNavigation />
    </div>
  );
};

export default Index;
